import React, { useState, useRef, useEffect } from 'react';
import { HelpTooltip } from './HelpTooltip';


export interface RichDropdownItem {
  value: string;
  title: string;
  subtitle?: string;
  thumbnail?: string;
}

interface RichDropdownWidgetProps {
  label: string;
  value: any;
  onChange: (value: any) => void;
  items: RichDropdownItem[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  description?: string;
  helpText?: string;
  displayMode?: 'dropdown' | 'tiles';
}

export const RichDropdownWidget: React.FC<RichDropdownWidgetProps> = ({
  label,
  value,
  onChange,
  items,
  placeholder = 'Select an option...',
  error,
  disabled = false,
  required = false,
  description,
  helpText,
  displayMode = 'dropdown'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the selected item
  const selectedItem = items.find(item => item.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: RichDropdownItem) => {
    onChange(item.value);
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <label className="text-label font-medium text-gray-700">
          {label}{required && <span className="text-error ml-0.5">*</span>}
        </label>
        {helpText && <HelpTooltip helpText={helpText} />}
      </div>
      {description && <p className="text-label text-gray-500">{description}</p>}

      {displayMode === 'tiles' ? (
        /* ======= TILES MODE ======= */
        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {items.map((item, index) => {
            const isSel = item.value === value;
            return (
              <button key={index} type="button" onClick={() => !disabled && handleSelect(item)}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all duration-base text-left ${
                  isSel ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white'
                }`}>
                {item.thumbnail && (
                  <img src={item.thumbnail} alt="" className="w-full aspect-square object-cover rounded-md" />
                )}
                <div className="flex flex-col min-w-0 w-full">
                  <span className={`text-label font-medium truncate ${isSel ? 'text-blue-700' : 'text-gray-900'}`}>{item.title}</span>
                  {item.subtitle && <span className="text-meta text-gray-500 truncate">{item.subtitle}</span>}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* ======= DROPDOWN MODE ======= */
        <div ref={dropdownRef} className="relative">
          <button type="button" onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 border rounded-md bg-white text-body text-left transition-[border-color,box-shadow] duration-fast outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
              error ? 'border-error' : isOpen ? 'border-blue-400 shadow-focus' : 'border-gray-300 hover:border-gray-400'
            }`}>
            {selectedItem ? (
              <div className="flex items-center gap-3 min-w-0">
                {selectedItem.thumbnail && (
                  <img src={selectedItem.thumbnail} alt="" className="w-9 h-9 rounded-md object-cover border border-gray-200 flex-shrink-0" />
                )}
                <div className="flex flex-col min-w-0">
                  <span className="text-label font-medium text-gray-900 truncate">{selectedItem.title}</span>
                  {selectedItem.subtitle && <span className="text-meta text-gray-500 truncate">{selectedItem.subtitle}</span>}
                </div>
              </div>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
            <span className={`text-gray-400 text-xs flex-shrink-0 transition-transform duration-fast ${isOpen ? 'rotate-180' : ''}`}>▼</span>
          </button>
          {isOpen && (
            <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto">
              {items.map((item, index) => {
                const isSel = item.value === value;
                return (
                  <button key={index} type="button" onClick={() => handleSelect(item)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors duration-fast ${isSel ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    {item.thumbnail && (
                      <img src={item.thumbnail} alt="" className="w-9 h-9 rounded-md object-cover border border-gray-200 flex-shrink-0" />
                    )}
                    <div className="flex-1 flex flex-col min-w-0">
                      <span className={`text-label font-medium truncate ${isSel ? 'text-blue-700' : 'text-gray-900'}`}>{item.title}</span>
                      {item.subtitle && <span className={`text-meta truncate ${isSel ? 'text-blue-500' : 'text-gray-500'}`}>{item.subtitle}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {error && <p className="text-label text-error">{error}</p>}
    </div>
  );
};

export default RichDropdownWidget;

